import { Typography, Stack } from '@mui/material';
import { Trans } from '@lingui/react/macro';
import Image, { StaticImageData } from 'next/image';
import ReductionsIcon from '@watershed/icons/components/Reductions';
import IndexList from '@watershed/ui-core/components/IndexList';
import { useUserContext } from '../../utils/UserContext';
import HomeCardBase from './HomeCardBase';
import { routeForReduxEnterprise } from '@watershed/shared-universal/dashboardRoutes';
import dac from './dac.png';
import reduce from './reduce.png';

interface ReductionPlanStep {
  title: string;
  description: string;
  imgUrl: StaticImageData;
}

const content = (): Array<ReductionPlanStep> => {
  return [
    {
      title: '1. Build a plan to reduce emissions',
      description:
        'Your organisation is on track to increase its emissions in the future as its business grows, but it has the opportunity to reduce these emissions by changing its processes.',
      imgUrl: dac,
    },
    {
      title: '2. Remove any remaining',
      description:
        'Once you have a plan for reducing emissions, you need to decide what to do about any emissions that you’re unable to eliminate. Popular options include purchasing a carbon removal portfolio to negate your remaining impact.',
      imgUrl: reduce,
    },
  ];
};

export default function NextReductionsCard({
  plans,
}: {
  plans: Array<{ id: string; name: string }>;
}) {
  const { orgName } = useUserContext();

  return (
    <HomeCardBase
      title={{ label: 'Reduction plans', icon: ReductionsIcon }}
      link={{
        label: 'View plans',
        to: routeForReduxEnterprise(),
        event: 'homeNextReductionsCardButton',
      }}
    >
      <Stack p={2} gap={2}>
        <Stack gap={2}>
          <Typography variant="body2">
            <Trans>
              {orgName} needs to make two decisions about its carbon footprint
              in the future:
            </Trans>
          </Typography>
          <IndexList
            items={content().map((step, i) => ({
              id: i,
              children: (
                <Stack direction="row" gap={2}>
                  <Image
                    src={step.imgUrl.src}
                    width={80}
                    height={80}
                    alt={step.title}
                  />
                  <Stack gap={0.25}>
                    <Typography variant="h4">{step.title}</Typography>
                    <Typography variant="body2">{step.description}</Typography>
                  </Stack>
                </Stack>
              ),
            }))}
          />
        </Stack>

        <Stack gap={2}>
          <Typography variant="body2">
            <Trans>
              Experiment these with different scenarios by playing with a
              reduction plan:
            </Trans>
          </Typography>
          <IndexList
            items={plans.map((plan) => ({
              id: plan.id,
              path: routeForReduxEnterprise({ planId: plan.id }),
              children: plan.name,
            }))}
          />
        </Stack>
      </Stack>
    </HomeCardBase>
  );
}
